import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Paperclip, Building2, User, X, FileText } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { printCustomerStatement } from "../../lib/rentalInvoicePrint";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { api, fileUrl } from "../../lib/api";

const EMPTY = { kind: "individual", name: "", id_number: "", cr_number: "", phone: "", email: "", license_number: "", license_expiry: "", nationality: "", attachments: [], notes: "" };

export const CustomerDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [up, setUp] = useState(false);
  useEffect(() => { if (open) setF(editing ? { ...EMPTY, ...editing } : EMPTY); }, [open, editing]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setUp(true);
    try {
      for (const file of files) { const r = await api.uploadFile(file); setF((p) => ({ ...p, attachments: [...p.attachments, { id: r.attachment_id, name: r.attachment_name }] })); }
    } catch { toast.error("تعذّر رفع الملف"); } finally { setUp(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const r = editing ? await api.updateRentalCustomer(editing.id, f) : await api.addRentalCustomer(f);
      toast.success("تم حفظ العميل"); onOpenChange(false); onSaved(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const company = f.kind === "company";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="rental-customer-dialog">
        <DialogHeader><DialogTitle>{editing ? "تعديل العميل" : "عميل جديد"}</DialogTitle><DialogDescription>بيانات المستأجر الخارجي ومستنداته.</DialogDescription></DialogHeader>
        <div className="flex gap-2">
          {[["individual", "فرد", User], ["company", "شركة", Building2]].map(([k, l, I]) => (
            <button key={k} type="button" data-testid={`rental-customer-kind-${k}`} onClick={() => setF({ ...f, kind: k })}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg border py-2 text-sm font-semibold ${f.kind === k ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}><I className="h-4 w-4" /> {l}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label>{company ? "اسم الشركة" : "الاسم"}</Label><Input data-testid="rental-customer-name" value={f.name} onChange={set("name")} /></div>
          {company
            ? <div className="space-y-1.5"><Label>السجل التجاري</Label><Input data-testid="rental-customer-cr" dir="ltr" value={f.cr_number} onChange={set("cr_number")} /></div>
            : <div className="space-y-1.5"><Label>رقم الهوية / الإقامة</Label><Input data-testid="rental-customer-idnum" dir="ltr" value={f.id_number} onChange={set("id_number")} /></div>}
          <div className="space-y-1.5"><Label>الجوال</Label><Input data-testid="rental-customer-phone" dir="ltr" value={f.phone} onChange={set("phone")} /></div>
          <div className="space-y-1.5"><Label>البريد الإلكتروني</Label><Input data-testid="rental-customer-email" type="email" dir="ltr" placeholder="name@example.com" value={f.email || ""} onChange={set("email")} /></div>
          {!company && <>
            <div className="space-y-1.5"><Label>رقم الرخصة</Label><Input data-testid="rental-customer-license" dir="ltr" value={f.license_number} onChange={set("license_number")} /></div>
            <div className="space-y-1.5"><Label>انتهاء الرخصة</Label><Input data-testid="rental-customer-license-expiry" type="date" dir="ltr" value={f.license_expiry} onChange={set("license_expiry")} /></div>
            <div className="space-y-1.5"><Label>الجنسية</Label><Input value={f.nationality} onChange={set("nationality")} /></div>
          </>}
        </div>
        <label className="flex items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-500 cursor-pointer hover:bg-slate-50" data-testid="rental-customer-attach">
          {up ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} إرفاق الهوية / الرخصة / السجل
          <input type="file" multiple accept="image/*,.pdf" className="hidden" onChange={pick} />
        </label>
        {f.attachments.map((a, i) => (
          <div key={a.id} className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1 text-xs"><span className="truncate">{a.name}</span>
            <button type="button" onClick={() => setF({ ...f, attachments: f.attachments.filter((_, j) => j !== i) })}><X className="h-3.5 w-3.5 text-slate-400" /></button></div>
        ))}
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="rental-customer-save" disabled={busy || up} onClick={save} className="bg-blue-600 hover:bg-blue-700">حفظ</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RentalCustomersTab = ({ canEdit }) => {
  const { company } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState({ open: false, editing: null });
  const load = useCallback(() => api.rentalCustomers().then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const del = async (c) => {
    if (!window.confirm(`حذف العميل ${c.name}؟`)) return;
    try { await api.deleteRentalCustomer(c.id); toast.success("تم الحذف"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  const statement = async (c) => {
    try { if (!printCustomerStatement(await api.rentalCustomerStatement(c.id), company)) toast.error("فعّل النوافذ المنبثقة للطباعة"); }
    catch { toast.error("تعذّر تحميل الكشف"); }
  };
  if (!rows) return <div className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div>;
  const list = rows.filter((c) => !q || `${c.name} ${c.phone} ${c.id_number} ${c.cr_number}`.includes(q));
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        {canEdit && <Button data-testid="rental-customer-add" onClick={() => setDlg({ open: true, editing: null })} className="bg-blue-600 hover:bg-blue-700 gap-1.5"><Plus className="h-4 w-4" /> عميل جديد</Button>}
        <Input data-testid="rental-customer-search" placeholder="بحث بالاسم أو الجوال أو الهوية" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" />
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-slate-500"><tr><th className="px-3 py-2.5 text-right">العميل</th><th className="px-3 py-2.5">الهوية / السجل</th><th className="px-3 py-2.5">الجوال</th><th className="px-3 py-2.5">الرخصة</th><th className="px-3 py-2.5"></th></tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400" data-testid="rental-customer-empty">لا يوجد عملاء</td></tr>}
            {list.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`rental-customer-row-${c.id}`}>
                <td className="px-3 py-2.5 text-right"><div className="font-semibold text-slate-800">{c.name}</div><div className="text-xs text-slate-400">{c.kind === "company" ? "شركة" : "فرد"}
                  {(c.attachments || []).map((a) => <a key={a.id} href={fileUrl(a.id)} target="_blank" rel="noreferrer" className="mr-1 text-blue-600"><Paperclip className="inline h-3 w-3" /></a>)}</div></td>
                <td className="px-3 py-2.5 text-center font-mono-nums" dir="ltr">{c.cr_number || c.id_number || "—"}</td>
                <td className="px-3 py-2.5 text-center font-mono-nums" dir="ltr">{c.phone || "—"}{c.email && <div className="text-[11px] text-slate-400" data-testid={`rental-customer-email-${c.id}`}>{c.email}</div>}</td>
                <td className="px-3 py-2.5 text-center text-xs">{c.license_number || "—"}{c.license_expiry && <div className={c.license_expiry < today ? "text-rose-600 font-semibold" : "text-slate-400"}>{c.license_expiry}</div>}</td>
                <td className="px-3 py-2.5"><div className="flex justify-center gap-1">
                  <button data-testid={`rental-customer-statement-${c.id}`} onClick={() => statement(c)} title="كشف حساب" className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"><FileText className="h-4 w-4" /></button>
                  {canEdit && <>
                  <button data-testid={`rental-customer-edit-${c.id}`} onClick={() => setDlg({ open: true, editing: c })} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`rental-customer-delete-${c.id}`} onClick={() => del(c)} className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button></>}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CustomerDialog open={dlg.open} editing={dlg.editing} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} onSaved={load} />
    </div>
  );
};
